import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "../lib/api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { fmtDuration } from "../lib/format";
import { Loader2, Save, Trash2, Plus } from "lucide-react";
import { useAuth } from "../context/AuthContext";

// unit -> seconds multiplier
const UNITS = { hours: 3600, days: 86400 };

function toValue(seconds) {
  if (seconds % 86400 === 0) return { value: seconds / 86400, unit: "days" };
  return { value: +(seconds / 3600).toFixed(2), unit: "hours" };
}

export default function AdminSettings() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data, isLoading } = useQuery({ queryKey: ["sla-settings"], queryFn: () => api.get("/admin/sla-settings").then((r) => r.data) });
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statusSaving, setStatusSaving] = useState(null);
  const [statusDeleting, setStatusDeleting] = useState(null);
  const statusesQ = useQuery({ queryKey: ["ticket-statuses"], queryFn: () => api.get("/ticket-statuses").then((r) => r.data) });
  const [statuses, setStatuses] = useState([]);
  const [newStatus, setNewStatus] = useState({ code: "", label: "", color: "#64748b", sort_order: 70, is_waiting: false });
  const [statusAdding, setStatusAdding] = useState(false);

  useEffect(() => {
    if (statusesQ.data) setStatuses(statusesQ.data);
  }, [statusesQ.data]);

  useEffect(() => {
    if (data) {
      setForm({ LOW: toValue(data.LOW), MEDIUM: toValue(data.MEDIUM), HIGH: toValue(data.HIGH) });
    }
  }, [data]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        LOW: Math.round(form.LOW.value * UNITS[form.LOW.unit]),
        MEDIUM: Math.round(form.MEDIUM.value * UNITS[form.MEDIUM.unit]),
        HIGH: Math.round(form.HIGH.value * UNITS[form.HIGH.unit]),
      };
      await api.put("/admin/sla-settings", payload);
      qc.invalidateQueries();
      toast.success("SLA settings saved");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveStatus = async (status) => {
    setStatusSaving(status.id);
    try {
      await api.put(`/super-admin/ticket-statuses/${status.id}`, status);
      qc.invalidateQueries({ queryKey: ["ticket-statuses"] });
      toast.success(`${status.code} status saved`);
    } catch (err) {
      toast.error(apiError(err.response?.data) || "Failed to save status");
    } finally {
      setStatusSaving(null);
    }
  };

  const deleteStatus = async (status) => {
    if (!window.confirm(`Delete the ${status.code} status configuration?`)) return;
    setStatusDeleting(status.id);
    try {
      await api.delete(`/super-admin/ticket-statuses/${status.id}`);
      setStatuses(statuses.filter((item) => item.id !== status.id));
      qc.invalidateQueries({ queryKey: ["ticket-statuses"] });
      toast.success(`${status.code} status deleted`);
    } catch (err) {
      toast.error(apiError(err.response?.data) || "Failed to delete status");
    } finally {
      setStatusDeleting(null);
    }
  };

  const addStatus = async () => {
    setStatusAdding(true);
    try {
      const { data: created } = await api.post("/super-admin/ticket-statuses", newStatus);
      setStatuses([...statuses, created].sort((a, b) => a.sort_order - b.sort_order));
      setNewStatus({ code: "", label: "", color: "#64748b", sort_order: 70, is_waiting: false });
      qc.invalidateQueries({ queryKey: ["ticket-statuses"] });
      toast.success("Ticket status added");
    } catch (err) {
      toast.error(apiError(err.response?.data) || "Failed to add status");
    } finally {
      setStatusAdding(false);
    }
  };

  if (isLoading || !form) return <Layout><div className="p-10 text-center text-muted-foreground">Loading…</div></Layout>;

  const Row = ({ p, color }) => (
    <div className="flex items-center gap-4 py-4 border-b border-border last:border-0">
      <div className={`w-24 font-heading font-bold ${color}`}>{p}</div>
      <div className="flex items-center gap-2">
        <Input type="number" min={1} className="w-24" value={form[p].value} data-testid={`sla-${p}-value`}
          onChange={(e) => setForm({ ...form, [p]: { ...form[p], value: Number(e.target.value) } })} />
        <Select value={form[p].unit} onValueChange={(u) => setForm({ ...form, [p]: { ...form[p], unit: u } })}>
          <SelectTrigger className="w-28" data-testid={`sla-${p}-unit`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="hours">Hours</SelectItem>
            <SelectItem value="days">Days</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="text-sm text-muted-foreground ml-auto">= {fmtDuration(form[p].value * UNITS[form[p].unit])}</div>
    </div>
  );

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="font-heading font-black tracking-tighter text-4xl">SLA Settings</h1>
        <p className="text-muted-foreground mt-1">Configure resolution targets per priority level</p>
      </div>

      <div className="space-y-8">
        <section className="grid items-start gap-6 lg:grid-cols-2">
          <div className="rounded-lg border border-border bg-card p-6">
            <Row p="HIGH" color="text-red-600" />
            <Row p="MEDIUM" color="text-amber-600" />
            <Row p="LOW" color="text-slate-500" />
            <Button onClick={save} disabled={saving} className="mt-6 gap-2" data-testid="save-sla-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Settings
            </Button>
          </div>
          <div className="rounded-lg border border-border bg-accent/40 p-5 text-sm">
            <div className="font-heading font-bold tracking-tight mb-2">How SLA is calculated</div>
            <ul className="space-y-1.5 text-muted-foreground list-disc pl-5">
              <li>The SLA clock starts when a ticket first moves to <b>In Progress</b> (<code>started_at</code>).</li>
              <li>The clock <b>pauses</b> during <b>Waiting Customer</b> and <b>Waiting Parts</b> states, and resumes when work continues.</li>
              <li>Resolution time = <code>done_at − started_at − paused time</code>.</li>
              <li>SLA is <b>Met</b> if resolution ≤ target, otherwise <b>Breached</b>. Active tickets show <b>At Risk</b> when under 20% time remains.</li>
            </ul>
          </div>
        </section>

      {user?.role === "super_admin" && (
        <section className="grid items-start gap-6 lg:grid-cols-2">
          <div className="rounded-lg border border-border bg-card p-6">
          <h2 className="font-heading font-bold tracking-tight text-lg">Ticket Statuses</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-4">Customize the display name and color for each workflow status.</p>
          <div className="grid grid-cols-[120px_1fr_52px_auto_auto_auto] items-center gap-3 mb-5 rounded-md bg-secondary/50 p-3">
            <Input placeholder="CODE" value={newStatus.code} onChange={(e) => setNewStatus({ ...newStatus, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "") })} aria-label="New status code" />
            <Input placeholder="Status name" value={newStatus.label} onChange={(e) => setNewStatus({ ...newStatus, label: e.target.value })} aria-label="New status name" />
            <input type="color" value={newStatus.color} onChange={(e) => setNewStatus({ ...newStatus, color: e.target.value })} className="h-10 w-12 cursor-pointer rounded border border-input bg-background p-1" aria-label="New status color" />
            <Input type="number" min={0} max={999} value={newStatus.sort_order} onChange={(e) => setNewStatus({ ...newStatus, sort_order: Number(e.target.value) })} aria-label="New status order" />
            <label className="flex items-center gap-1 text-xs text-muted-foreground"><input type="checkbox" checked={newStatus.is_waiting} onChange={(e) => setNewStatus({ ...newStatus, is_waiting: e.target.checked })} /> Waiting</label>
            <Button onClick={addStatus} disabled={statusAdding || !newStatus.code || !newStatus.label} size="sm" aria-label="Add ticket status">{statusAdding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}</Button>
          </div>
          <div className="space-y-3">
            {statuses.map((status) => (
              <div key={status.id} className="grid grid-cols-[120px_1fr_52px_auto_auto] items-center gap-3 border-b border-border pb-3 last:border-0">
                <Label className="font-mono-plex text-xs">{status.code}</Label>
                <Input value={status.label} onChange={(e) => setStatuses(statuses.map((item) => item.id === status.id ? { ...item, label: e.target.value } : item))} />
                <input type="color" value={status.color} onChange={(e) => setStatuses(statuses.map((item) => item.id === status.id ? { ...item, color: e.target.value } : item))} className="h-10 w-12 cursor-pointer rounded border border-input bg-background p-1" aria-label={`${status.code} color`} />
                <label className="flex items-center gap-1 text-xs text-muted-foreground"><input type="checkbox" checked={status.is_waiting} onChange={(e) => setStatuses(statuses.map((item) => item.id === status.id ? { ...item, is_waiting: e.target.checked } : item))} /> Waiting</label>
                <div className="flex gap-1">
                  <Button onClick={() => saveStatus(status)} disabled={statusSaving === status.id || statusDeleting === status.id} size="sm" aria-label={`Save ${status.code} status`}>{statusSaving === status.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}</Button>
                  <Button onClick={() => deleteStatus(status)} disabled={statusSaving === status.id || statusDeleting === status.id} size="sm" variant="ghost" className="text-destructive hover:text-destructive" aria-label={`Delete ${status.code} status`}>{statusDeleting === status.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}</Button>
                </div>
              </div>
            ))}
          </div>
          </div>
          <div className="rounded-lg border border-border bg-accent/40 p-5 text-sm">
            <div className="font-heading font-bold tracking-tight mb-2">How ticket statuses work</div>
            <ul className="space-y-1.5 text-muted-foreground list-disc pl-5">
              <li><b>Code</b> is the unique internal identifier used by the ticket workflow.</li>
              <li><b>Status name</b> is the label displayed to customers, admins, and technicians.</li>
              <li><b>Color</b> controls the status badge color throughout the application.</li>
              <li><b>Order</b> controls the order in status filters and ticket status lists.</li>
              <li><b>Waiting</b> pauses the SLA timer while a ticket has this status. Uncheck it when the status should continue counting SLA time.</li>
              <li>Changes apply after clicking the save button on the status row.</li>
            </ul>
          </div>
        </section>
      )}

      </div>
    </Layout>
  );
}
